import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from './api';
import Spinner from './Spinner';

function asArray(data) {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    return [];
}

function formatError(err) {
    const data = err?.response?.data;
    if (!data) return err?.message || 'Request failed';
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    if (data.error) return data.error;
    const parts = Object.entries(data).map(([key, value]) => {
        const msg = Array.isArray(value) ? value.join(' ') : String(value);
        return `${key}: ${msg}`;
    });
    return parts.join(' | ') || 'Request failed';
}

const BADGE_COLORS = {
    Available: { bg: '#d1e7dd', text: '#0f5132', darkBg: '#1b4332', darkText: '#b7f0c8' },
    'Temporary assignment': { bg: '#cfe2ff', text: '#084298', darkBg: '#1a3358', darkText: '#9ec5fe' },
    'Temporary out': { bg: '#e2d9f3', text: '#432874', darkBg: '#3b2a5c', darkText: '#d4c2ff' },
    Absent: { bg: '#f8d7da', text: '#842029', darkBg: '#5c1a1f', darkText: '#f1aeb5' },
    Unavailable: { bg: '#f8d7da', text: '#842029', darkBg: '#5c1a1f', darkText: '#f1aeb5' },
    LOA: { bg: '#fff3cd', text: '#664d03', darkBg: '#5c4a12', darkText: '#ffe8a3' },
    Unassigned: { bg: '#e2e3e5', text: '#41464b', darkBg: '#3a3a3a', darkText: '#d0d0d0' },
};

function badgeStyle(badge, isDark) {
    const palette = BADGE_COLORS[badge] || {
        bg: '#e9ecef',
        text: '#212529',
        darkBg: '#333',
        darkText: '#ddd',
    };
    return {
        backgroundColor: isDark ? palette.darkBg : palette.bg,
        color: isDark ? palette.darkText : palette.text,
    };
}

function getStyles(theme) {
    const isDark = theme === 'dark';
    return {
        isDark,
        hint: { color: isDark ? '#9aa6b2' : '#6c757d', fontSize: '13px', margin: '0 0 14px' },
        filters: {
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            alignItems: 'end',
            marginBottom: '16px',
        },
        formGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
        label: { fontWeight: 600, fontSize: '13px' },
        input: {
            padding: '10px',
            borderRadius: '4px',
            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
            backgroundColor: isDark ? '#2a2a2a' : '#fff',
            color: isDark ? '#e0e0e0' : '#212529',
            fontSize: '14px',
            minWidth: '140px',
        },
        button: {
            padding: '10px 14px',
            backgroundColor: isDark ? '#6cb2f7' : '#0d6efd',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
        },
        secondaryButton: {
            padding: '10px 14px',
            backgroundColor: 'transparent',
            color: isDark ? '#e0e0e0' : '#212529',
            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
        },
        dangerButton: {
            padding: '6px 10px',
            backgroundColor: '#dc3545',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            marginTop: '6px',
            marginRight: '6px',
        },
        smallButton: {
            padding: '6px 10px',
            backgroundColor: isDark ? '#6cb2f7' : '#0d6efd',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            marginTop: '6px',
            marginRight: '6px',
        },
        summaryBar: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
            gap: '10px',
            marginBottom: '18px',
        },
        summaryCard: {
            backgroundColor: isDark ? '#252525' : '#f8f9fa',
            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
            borderRadius: '8px',
            padding: '12px',
            textAlign: 'center',
        },
        summaryNumber: { fontSize: '1.35rem', fontWeight: 700 },
        summaryLabel: { fontSize: '0.75rem', color: isDark ? '#9aa6b2' : '#6c757d' },
        board: {
            display: 'flex',
            gap: '16px',
            overflowX: 'auto',
            paddingBottom: '12px',
            alignItems: 'flex-start',
        },
        column: {
            minWidth: '300px',
            maxWidth: '340px',
            flex: '0 0 300px',
            backgroundColor: isDark ? '#1a1a1a' : '#f1f3f5',
            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
            borderRadius: '10px',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '72vh',
        },
        columnWarn: {
            borderColor: isDark ? '#c9a227' : '#ffc107',
        },
        columnHeader: {
            padding: '14px',
            borderBottom: `1px solid ${isDark ? '#333' : '#dee2e6'}`,
            backgroundColor: isDark ? '#222' : '#e9ecef',
            borderRadius: '10px 10px 0 0',
            position: 'sticky',
            top: 0,
            zIndex: 1,
        },
        columnTitle: { margin: 0, fontSize: '16px', fontWeight: 700 },
        columnMeta: {
            marginTop: '6px',
            fontSize: '12px',
            color: isDark ? '#9aa6b2' : '#6c757d',
        },
        metrics: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' },
        metricChip: {
            fontSize: '11px',
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: '999px',
            backgroundColor: isDark ? '#333' : '#fff',
            border: `1px solid ${isDark ? '#555' : '#ced4da'}`,
        },
        warnText: {
            color: isDark ? '#ffc107' : '#996c00',
            fontWeight: 700,
            fontSize: '12px',
            marginTop: '6px',
        },
        columnBody: {
            padding: '12px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
        },
        sectionLabel: {
            fontSize: '11px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: isDark ? '#9aa6b2' : '#6c757d',
            margin: '4px 0',
        },
        employeeCard: {
            backgroundColor: isDark ? '#2a2a2a' : '#fff',
            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
            borderRadius: '8px',
            padding: '12px',
            textAlign: 'left',
            width: '100%',
            color: 'inherit',
            fontFamily: 'inherit',
        },
        nameRow: {
            display: 'flex',
            justifyContent: 'space-between',
            gap: '8px',
            alignItems: 'flex-start',
            marginBottom: '6px',
        },
        name: { fontWeight: 700, fontSize: '14px' },
        empNumber: { fontSize: '11px', color: isDark ? '#9aa6b2' : '#6c757d' },
        detail: {
            fontSize: '12px',
            color: isDark ? '#c5cdd6' : '#495057',
            lineHeight: 1.45,
        },
        badge: {
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '999px',
            fontSize: '11px',
            fontWeight: 700,
            whiteSpace: 'nowrap',
        },
        empty: {
            padding: '20px',
            textAlign: 'center',
            color: isDark ? '#9aa6b2' : '#6c757d',
        },
        modalBackdrop: {
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            zIndex: 3000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
        },
        modal: {
            backgroundColor: isDark ? '#1e1e1e' : '#fff',
            color: isDark ? '#e0e0e0' : '#212529',
            borderRadius: '10px',
            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
            padding: '20px',
            width: '100%',
            maxWidth: '520px',
            maxHeight: '90vh',
            overflowY: 'auto',
        },
        modalTitle: { margin: '0 0 14px', fontSize: '18px' },
        formGrid: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
        },
        buttonRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '16px' },
        quickActions: {
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            marginBottom: '14px',
        },
    };
}

function EmployeeBoardCard({ emp, styles, onSelect, onEndTemp, onEndAbsence }) {
    return (
        <div style={styles.employeeCard}>
            <div style={styles.nameRow}>
                <button
                    type="button"
                    onClick={() => onSelect?.(emp)}
                    style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        textAlign: 'left',
                        cursor: 'pointer',
                        color: 'inherit',
                        fontFamily: 'inherit',
                    }}
                >
                    <div style={styles.name}>{emp.display_name}</div>
                    <div style={styles.empNumber}>#{emp.employee_number}</div>
                </button>
                <span
                    style={{
                        ...styles.badge,
                        ...badgeStyle(emp.operational_badge, styles.isDark),
                    }}
                >
                    {emp.operational_badge}
                </span>
            </div>
            <div style={styles.detail}>
                {(emp.position_title || emp.job_title || 'No title') +
                    (emp.shift_name ? ` · ${emp.shift_name}` : '')}
            </div>
            {emp.home_work_center_code && emp.role === 'temporary_in' && (
                <div style={styles.detail}>Home: {emp.home_work_center_code}</div>
            )}
            {emp.temp_work_center_code && emp.role === 'temporary_out' && (
                <div style={styles.detail}>Temp at: {emp.temp_work_center_code}</div>
            )}
            {emp.return_date && <div style={styles.detail}>Return: {emp.return_date}</div>}
            {emp.temp_end && <div style={styles.detail}>Temp ends: {emp.temp_end}</div>}
            {emp.temp_assignment_id && (
                <button
                    type="button"
                    style={styles.dangerButton}
                    onClick={() => onEndTemp?.(emp)}
                >
                    End temp
                </button>
            )}
            {emp.absence_id && emp.on_leave && (
                <button
                    type="button"
                    style={styles.dangerButton}
                    onClick={() => onEndAbsence?.(emp)}
                >
                    End absence
                </button>
            )}
        </div>
    );
}

function ManningDailyAvailability({
    theme,
    sites,
    workCenters,
    shifts,
    positions = [],
    onSelectEmployee,
    onNotify,
    onChanged,
}) {
    const styles = useMemo(() => getStyles(theme), [theme]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [filters, setFilters] = useState({
        as_of: new Date().toISOString().slice(0, 10),
        site: '',
        work_center: '',
        shift: '',
        search: '',
    });
    const [tempModal, setTempModal] = useState(false);
    const [absenceModal, setAbsenceModal] = useState(false);
    const [tempForm, setTempForm] = useState({
        employee: '',
        site: '',
        work_center: '',
        position: '',
        shift: '',
        effective_start: new Date().toISOString().slice(0, 10),
        effective_end: '',
        reason: '',
    });
    const [absenceForm, setAbsenceForm] = useState({
        employee: '',
        as_of: new Date().toISOString().slice(0, 10),
        notes: '',
    });

    const filteredWorkCenters = useMemo(() => {
        if (!filters.site) return workCenters.filter((wc) => wc.is_active);
        return workCenters.filter(
            (wc) => wc.is_active && String(wc.site) === String(filters.site)
        );
    }, [workCenters, filters.site]);

    const filteredShifts = useMemo(() => {
        if (!filters.site) return shifts.filter((s) => s.is_active);
        return shifts.filter((s) => s.is_active && String(s.site) === String(filters.site));
    }, [shifts, filters.site]);

    const tempWorkCenters = useMemo(() => {
        if (!tempForm.site) return workCenters.filter((wc) => wc.is_active);
        return workCenters.filter(
            (wc) => wc.is_active && String(wc.site) === String(tempForm.site)
        );
    }, [workCenters, tempForm.site]);

    const tempShifts = useMemo(() => {
        if (!tempForm.site) return shifts.filter((s) => s.is_active);
        return shifts.filter((s) => s.is_active && String(s.site) === String(tempForm.site));
    }, [shifts, tempForm.site]);

    const tempPositions = useMemo(() => {
        const active = positions.filter((p) => p.is_active);
        if (!tempForm.work_center) return active;
        return active.filter((p) => String(p.work_center) === String(tempForm.work_center));
    }, [positions, tempForm.work_center]);

    const selectedTempPosition = useMemo(
        () => positions.find((p) => String(p.id) === String(tempForm.position)) || null,
        [positions, tempForm.position]
    );

    const loadBoard = useCallback(async () => {
        setLoading(true);
        try {
            const params = { as_of: filters.as_of };
            if (filters.site) params.site = filters.site;
            if (filters.work_center) params.work_center = filters.work_center;
            if (filters.shift) params.shift = filters.shift;
            if (filters.search) params.search = filters.search;
            const [boardRes, empRes] = await Promise.all([
                api.get('/employees/daily-availability/', { params }),
                api.get('/employees/'),
            ]);
            setData(boardRes.data);
            setEmployees(asArray(empRes.data));
        } catch (err) {
            onNotify?.(formatError(err), 'error');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [filters, onNotify]);

    useEffect(() => {
        loadBoard();
    }, [loadBoard]);

    const activeEmployees = useMemo(
        () =>
            employees.filter((e) =>
                ['Active', 'On Extended LOA', 'Pending Start'].includes(e.status)
            ),
        [employees]
    );

    const createTempAssignment = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            await api.post('/employee-assignments/', {
                employee: Number(tempForm.employee),
                site: Number(tempForm.site),
                work_center: Number(tempForm.work_center),
                position: tempForm.position ? Number(tempForm.position) : null,
                shift: tempForm.shift ? Number(tempForm.shift) : null,
                assignment_type: 'Temporary',
                effective_start: tempForm.effective_start,
                effective_end: tempForm.effective_end || null,
                reason: tempForm.reason || 'Temporary support',
            });
            onNotify?.('Temporary assignment created');
            setTempModal(false);
            setTempForm({
                employee: '',
                site: '',
                work_center: '',
                position: '',
                shift: '',
                effective_start: filters.as_of,
                effective_end: '',
                reason: '',
            });
            await loadBoard();
            onChanged?.();
        } catch (err) {
            onNotify?.(formatError(err), 'error');
        } finally {
            setSaving(false);
        }
    };

    const createUnscheduledAbsence = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            await api.post('/absence-events/unscheduled/', {
                employee: Number(absenceForm.employee),
                as_of: absenceForm.as_of || filters.as_of,
                notes: absenceForm.notes || 'Unscheduled absence',
            });
            onNotify?.('Unscheduled absence recorded');
            setAbsenceModal(false);
            setAbsenceForm({
                employee: '',
                as_of: filters.as_of,
                notes: '',
            });
            await loadBoard();
            onChanged?.();
        } catch (err) {
            onNotify?.(formatError(err), 'error');
        } finally {
            setSaving(false);
        }
    };

    const endTemp = async (emp) => {
        if (!emp.temp_assignment_id) return;
        if (!window.confirm(`End temporary assignment for ${emp.display_name}?`)) return;
        setSaving(true);
        try {
            await api.post(`/employee-assignments/${emp.temp_assignment_id}/end_assignment/`, {
                effective_end: filters.as_of,
            });
            onNotify?.('Temporary assignment ended');
            await loadBoard();
            onChanged?.();
        } catch (err) {
            onNotify?.(formatError(err), 'error');
        } finally {
            setSaving(false);
        }
    };

    const endAbsence = async (emp) => {
        if (!emp.absence_id) return;
        if (!window.confirm(`End absence for ${emp.display_name}?`)) return;
        setSaving(true);
        try {
            await api.post(`/absence-events/${emp.absence_id}/end_absence/`);
            onNotify?.('Absence ended');
            await loadBoard();
            onChanged?.();
        } catch (err) {
            onNotify?.(formatError(err), 'error');
        } finally {
            setSaving(false);
        }
    };

    const totals = data?.totals;

    return (
        <div>
            <p style={styles.hint}>
                Daily operational manning picture. Temporary assignments move people without changing
                permanent home work center. Unscheduled absences update availability immediately.
            </p>

            <div style={styles.quickActions}>
                <button
                    type="button"
                    style={styles.button}
                    onClick={() => {
                        setTempForm((prev) => ({
                            ...prev,
                            effective_start: filters.as_of,
                            site: filters.site || prev.site,
                        }));
                        setTempModal(true);
                    }}
                >
                    Temporary assignment
                </button>
                <button
                    type="button"
                    style={styles.secondaryButton}
                    onClick={() => {
                        setAbsenceForm({ employee: '', as_of: filters.as_of, notes: '' });
                        setAbsenceModal(true);
                    }}
                >
                    Unscheduled absence
                </button>
            </div>

            <div style={styles.filters}>
                <div style={styles.formGroup}>
                    <label style={styles.label}>Date</label>
                    <input
                        style={styles.input}
                        type="date"
                        value={filters.as_of}
                        onChange={(e) =>
                            setFilters((prev) => ({ ...prev, as_of: e.target.value }))
                        }
                    />
                </div>
                <button
                    type="button"
                    style={styles.secondaryButton}
                    onClick={() =>
                        setFilters((prev) => ({
                            ...prev,
                            as_of: new Date().toISOString().slice(0, 10),
                        }))
                    }
                >
                    Today
                </button>
                <div style={styles.formGroup}>
                    <label style={styles.label}>Site</label>
                    <select
                        style={styles.input}
                        value={filters.site}
                        onChange={(e) =>
                            setFilters((prev) => ({
                                ...prev,
                                site: e.target.value,
                                work_center: '',
                                shift: '',
                            }))
                        }
                    >
                        <option value="">All sites</option>
                        {sites.map((site) => (
                            <option key={site.id} value={site.id}>
                                {site.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={styles.formGroup}>
                    <label style={styles.label}>Work center</label>
                    <select
                        style={styles.input}
                        value={filters.work_center}
                        onChange={(e) =>
                            setFilters((prev) => ({ ...prev, work_center: e.target.value }))
                        }
                    >
                        <option value="">All work centers</option>
                        {filteredWorkCenters.map((wc) => (
                            <option key={wc.id} value={wc.id}>
                                {wc.code} — {wc.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={styles.formGroup}>
                    <label style={styles.label}>Shift</label>
                    <select
                        style={styles.input}
                        value={filters.shift}
                        onChange={(e) =>
                            setFilters((prev) => ({ ...prev, shift: e.target.value }))
                        }
                    >
                        <option value="">All shifts</option>
                        {filteredShifts.map((shift) => (
                            <option key={shift.id} value={shift.id}>
                                {shift.code} — {shift.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={styles.formGroup}>
                    <label style={styles.label}>Search</label>
                    <input
                        style={styles.input}
                        placeholder="Name, number…"
                        value={filters.search}
                        onChange={(e) =>
                            setFilters((prev) => ({ ...prev, search: e.target.value }))
                        }
                    />
                </div>
                <button type="button" style={styles.button} onClick={loadBoard}>
                    Apply
                </button>
            </div>

            {totals && (
                <div style={styles.summaryBar}>
                    {[
                        ['Available', totals.available],
                        ['Absent', totals.absent],
                        ['Temp in', totals.temporary_in],
                        ['Temp out', totals.temporary_out],
                        ['Assigned', totals.assigned],
                        ['Vacancies', totals.vacancies],
                        ['Below min', totals.below_minimum],
                        ['Unassigned', totals.unassigned],
                    ].map(([label, value]) => (
                        <div key={label} style={styles.summaryCard}>
                            <div style={styles.summaryNumber}>{value ?? 0}</div>
                            <div style={styles.summaryLabel}>{label}</div>
                        </div>
                    ))}
                </div>
            )}

            {loading && (
                <div style={styles.empty}>
                    <Spinner />
                </div>
            )}

            {!loading && data && (
                <div style={styles.board}>
                    {data.work_centers.map((wc) => (
                        <div
                            key={wc.id}
                            style={{
                                ...styles.column,
                                ...(wc.below_minimum ? styles.columnWarn : {}),
                            }}
                        >
                            <div style={styles.columnHeader}>
                                <h3 style={styles.columnTitle}>
                                    {wc.code} — {wc.name}
                                </h3>
                                <div style={styles.columnMeta}>{wc.site_name}</div>
                                <div style={styles.metrics}>
                                    <span style={styles.metricChip}>
                                        {wc.available_count}/{wc.required_count || '—'} avail
                                    </span>
                                    <span style={styles.metricChip}>
                                        {wc.absent_count} absent
                                    </span>
                                    <span style={styles.metricChip}>
                                        +{wc.temporary_in_count}/−{wc.temporary_out_count} temp
                                    </span>
                                    <span style={styles.metricChip}>
                                        {wc.vacancy_count} vacant
                                    </span>
                                </div>
                                {wc.below_minimum && (
                                    <div style={styles.warnText}>
                                        Below minimum staffing ({wc.minimum_staffing})
                                    </div>
                                )}
                            </div>
                            <div style={styles.columnBody}>
                                {wc.available.length > 0 && (
                                    <>
                                        <div style={styles.sectionLabel}>Available</div>
                                        {wc.available.map((emp) => (
                                            <EmployeeBoardCard
                                                key={`avail-${emp.id}`}
                                                emp={emp}
                                                styles={styles}
                                                onSelect={onSelectEmployee}
                                                onEndTemp={endTemp}
                                                onEndAbsence={endAbsence}
                                            />
                                        ))}
                                    </>
                                )}
                                {wc.absent.length > 0 && (
                                    <>
                                        <div style={styles.sectionLabel}>Absent</div>
                                        {wc.absent.map((emp) => (
                                            <EmployeeBoardCard
                                                key={`abs-${emp.id}`}
                                                emp={emp}
                                                styles={styles}
                                                onSelect={onSelectEmployee}
                                                onEndTemp={endTemp}
                                                onEndAbsence={endAbsence}
                                            />
                                        ))}
                                    </>
                                )}
                                {wc.temporary_out.length > 0 && (
                                    <>
                                        <div style={styles.sectionLabel}>Temporary out</div>
                                        {wc.temporary_out.map((emp) => (
                                            <EmployeeBoardCard
                                                key={`out-${emp.id}`}
                                                emp={emp}
                                                styles={styles}
                                                onSelect={onSelectEmployee}
                                                onEndTemp={endTemp}
                                                onEndAbsence={endAbsence}
                                            />
                                        ))}
                                    </>
                                )}
                                {wc.available.length === 0 &&
                                    wc.absent.length === 0 &&
                                    wc.temporary_out.length === 0 && (
                                        <div style={styles.empty}>No personnel for this date</div>
                                    )}
                            </div>
                        </div>
                    ))}

                    {data.unassigned?.length > 0 && (
                        <div style={styles.column}>
                            <div style={styles.columnHeader}>
                                <h3 style={styles.columnTitle}>Unassigned</h3>
                                <div style={styles.columnMeta}>
                                    No current primary assignment
                                </div>
                                <div style={styles.metrics}>
                                    <span style={styles.metricChip}>
                                        {data.unassigned.length} people
                                    </span>
                                </div>
                            </div>
                            <div style={styles.columnBody}>
                                {data.unassigned.map((emp) => (
                                    <EmployeeBoardCard
                                        key={`un-${emp.id}`}
                                        emp={emp}
                                        styles={styles}
                                        onSelect={onSelectEmployee}
                                        onEndTemp={endTemp}
                                        onEndAbsence={endAbsence}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {data.work_centers.length === 0 && data.unassigned?.length === 0 && (
                        <div style={styles.empty}>No data for the selected filters.</div>
                    )}
                </div>
            )}

            {tempModal && (
                <div style={styles.modalBackdrop} onClick={() => setTempModal(false)}>
                    <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.modalTitle}>Temporary assignment</h3>
                        <form onSubmit={createTempAssignment}>
                            <div style={styles.formGrid}>
                                <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                                    <label style={styles.label}>Employee</label>
                                    <select
                                        style={styles.input}
                                        required
                                        value={tempForm.employee}
                                        onChange={(e) =>
                                            setTempForm((prev) => ({
                                                ...prev,
                                                employee: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">Select employee</option>
                                        {activeEmployees.map((emp) => (
                                            <option key={emp.id} value={emp.id}>
                                                {emp.display_name} ({emp.employee_number})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Site</label>
                                    <select
                                        style={styles.input}
                                        required
                                        value={tempForm.site}
                                        onChange={(e) =>
                                            setTempForm((prev) => ({
                                                ...prev,
                                                site: e.target.value,
                                                work_center: '',
                                                position: '',
                                                shift: '',
                                            }))
                                        }
                                    >
                                        <option value="">Select site</option>
                                        {sites.map((site) => (
                                            <option key={site.id} value={site.id}>
                                                {site.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Temp work center</label>
                                    <select
                                        style={styles.input}
                                        required
                                        value={tempForm.work_center}
                                        onChange={(e) =>
                                            setTempForm((prev) => ({
                                                ...prev,
                                                work_center: e.target.value,
                                                position: '',
                                            }))
                                        }
                                    >
                                        <option value="">Select work center</option>
                                        {tempWorkCenters.map((wc) => (
                                            <option key={wc.id} value={wc.id}>
                                                {wc.code} — {wc.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Internal title</label>
                                    <select
                                        style={styles.input}
                                        value={tempForm.position}
                                        onChange={(e) =>
                                            setTempForm((prev) => ({
                                                ...prev,
                                                position: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">Optional</option>
                                        {tempPositions.map((pos) => (
                                            <option key={pos.id} value={pos.id}>
                                                {pos.title}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Government title</label>
                                    <input
                                        style={{
                                            ...styles.input,
                                            backgroundColor:
                                                theme === 'dark' ? '#2a2a2a' : '#f3f4f6',
                                        }}
                                        readOnly
                                        value={selectedTempPosition?.government_title || ''}
                                        placeholder="Auto-fills from internal title"
                                    />
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Shift (optional)</label>
                                    <select
                                        style={styles.input}
                                        value={tempForm.shift}
                                        onChange={(e) =>
                                            setTempForm((prev) => ({
                                                ...prev,
                                                shift: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">Optional</option>
                                        {tempShifts.map((shift) => (
                                            <option key={shift.id} value={shift.id}>
                                                {shift.code} — {shift.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Start</label>
                                    <input
                                        style={styles.input}
                                        type="date"
                                        required
                                        value={tempForm.effective_start}
                                        onChange={(e) =>
                                            setTempForm((prev) => ({
                                                ...prev,
                                                effective_start: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>End</label>
                                    <input
                                        style={styles.input}
                                        type="date"
                                        value={tempForm.effective_end}
                                        onChange={(e) =>
                                            setTempForm((prev) => ({
                                                ...prev,
                                                effective_end: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                                <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                                    <label style={styles.label}>Reason</label>
                                    <input
                                        style={styles.input}
                                        value={tempForm.reason}
                                        onChange={(e) =>
                                            setTempForm((prev) => ({
                                                ...prev,
                                                reason: e.target.value,
                                            }))
                                        }
                                        placeholder="Surge support, loan, coverage…"
                                    />
                                </div>
                            </div>
                            <div style={styles.buttonRow}>
                                <button type="submit" style={styles.button} disabled={saving}>
                                    {saving ? 'Saving…' : 'Create temporary assignment'}
                                </button>
                                <button
                                    type="button"
                                    style={styles.secondaryButton}
                                    onClick={() => setTempModal(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {absenceModal && (
                <div style={styles.modalBackdrop} onClick={() => setAbsenceModal(false)}>
                    <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.modalTitle}>Unscheduled absence</h3>
                        <form onSubmit={createUnscheduledAbsence}>
                            <div style={styles.formGrid}>
                                <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                                    <label style={styles.label}>Employee</label>
                                    <select
                                        style={styles.input}
                                        required
                                        value={absenceForm.employee}
                                        onChange={(e) =>
                                            setAbsenceForm((prev) => ({
                                                ...prev,
                                                employee: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">Select employee</option>
                                        {activeEmployees.map((emp) => (
                                            <option key={emp.id} value={emp.id}>
                                                {emp.display_name} ({emp.employee_number})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Date</label>
                                    <input
                                        style={styles.input}
                                        type="date"
                                        required
                                        value={absenceForm.as_of}
                                        onChange={(e) =>
                                            setAbsenceForm((prev) => ({
                                                ...prev,
                                                as_of: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                                <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                                    <label style={styles.label}>Notes</label>
                                    <input
                                        style={styles.input}
                                        value={absenceForm.notes}
                                        onChange={(e) =>
                                            setAbsenceForm((prev) => ({
                                                ...prev,
                                                notes: e.target.value,
                                            }))
                                        }
                                        placeholder="Call-out / no-show notes"
                                    />
                                </div>
                            </div>
                            <div style={styles.buttonRow}>
                                <button type="submit" style={styles.button} disabled={saving}>
                                    {saving ? 'Saving…' : 'Record absence'}
                                </button>
                                <button
                                    type="button"
                                    style={styles.secondaryButton}
                                    onClick={() => setAbsenceModal(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ManningDailyAvailability;
